import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import { Separator } from './ui/separator';
import { Progress } from './ui/progress';
import {
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  Trophy,
  TrendingUp,
  Users,
  Target,
  BarChart3,
  Settings,
  Eye,
  Calendar,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import ABTestEditDialog from './ABTestEditDialog';

export default function ABTesting() {
  const { user } = useAuth();
  const { t } = useT();
  const [tests, setTests] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testResults, setTestResults] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    campaign_id: '',
    name: '',
    description: '',
    test_type: 'landing_page',
    confidence_level: 95,
    min_sample_size: 100,
    auto_optimization: false,
    variants: [
      { id: 'control', name: 'Control', url: '', weight: 50 },
      { id: 'variant_a', name: 'Variant A', url: '', weight: 50 }
    ]
  });

  useEffect(() => {
    Promise.all([fetchABTests(), fetchCampaigns(), fetchLandingPages(), fetchOffers()]);
  }, []);

  const fetchABTests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ab-tests`);
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching A/B tests:', error);
      toast.error(t('Failed to load A/B tests'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchLandingPages = async () => {
    try {
      const response = await axios.get(`${API}/landing-pages`);
      setLandingPages(response.data);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers`);
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTest, setEditTest] = useState(null);

  const handleDeleteTest = async (testId) => {
    try {
      await axios.delete(`${API}/ab-tests/${testId}`);
      setTests(tests.filter(t => t.id !== testId));
      setDeleteTarget(null);
      toast.success(t('A/B test deleted'));
    } catch (error) {
      toast.error(t('Failed to delete test'));
    }
  };

  const handleCreateTest = async () => {
    try {
      const testPayload = {
        ...formData,
        traffic_split: formData.variants.reduce((acc, variant) => {
          acc[variant.id] = variant.weight;
          return acc;
        }, {}),
        variants: formData.variants.map(v => ({
          id: v.id,
          name: v.name,
          url: v.url,
          type: formData.test_type
        }))
      };

      const response = await axios.post(`${API}/ab-tests`, testPayload);
      setTests([...tests, response.data]);
      setCreateDialogOpen(false);
      resetForm();
      toast.success(t('A/B test created'));
    } catch (error) {
      console.error('Error creating A/B test:', error);
      toast.error(t('Failed to create A/B test'));
    }
  };

  const handleStartTest = async (testId) => {
    try {
      await axios.post(`${API}/ab-tests/${testId}/start`);
      setTests(tests.map(test =>
        test.id === testId ? { ...test, status: 'running' } : test
      ));
      toast.success(t('A/B test started'));
    } catch (error) {
      console.error('Error starting test:', error);
      toast.error(t('Failed to start test'));
    }
  };

  const handlePauseTest = async (testId) => {
    try {
      await axios.post(`${API}/ab-tests/${testId}/pause`);
      setTests(tests.map(test =>
        test.id === testId ? { ...test, status: 'paused' } : test
      ));
      toast.success(t('A/B test paused'));
    } catch (error) {
      console.error('Error pausing test:', error);
      toast.error(t('Failed to pause test'));
    }
  };

  const fetchTestResults = async (testId) => {
    try {
      const response = await axios.get(`${API}/ab-tests/${testId}/results`);
      setTestResults(response.data);
      setResultsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching test results:', error);
      toast.error(t('Failed to load results'));
    }
  };

  const addVariant = () => {
    setFormData(prev => {
      const letter = String.fromCharCode(65 + prev.variants.length - 1);
      return {
        ...prev,
        variants: [
          ...prev.variants,
          {
            id: `variant_${letter}`,
            name: `Variant ${letter}`,
            url: '',
            weight: Math.floor(100 / (prev.variants.length + 1))
          }
        ]
      };
    });

    // Redistribute weights evenly
    redistributeWeights();
  };

  const removeVariant = (index) => {
    if (formData.variants.length <= 2) {
      toast.error(t('At least 2 variants required'));
      return;
    }

    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));

    redistributeWeights();
  };

  const redistributeWeights = () => {
    setTimeout(() => {
      setFormData(prev => {
        if (!prev.variants.length) return prev;
        const equalWeight = Math.floor(100 / prev.variants.length);
        const remainder = 100 - (equalWeight * prev.variants.length);

        return {
          ...prev,
          variants: prev.variants.map((variant, index) => ({
            ...variant,
            weight: index === 0 ? equalWeight + remainder : equalWeight
          }))
        };
      });
    }, 0);
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) =>
        i === index ? { ...variant, [field]: value } : variant
      )
    }));
  };

  const resetForm = () => {
    setFormData({
      campaign_id: '',
      name: '',
      description: '',
      test_type: 'landing_page',
      confidence_level: 95,
      min_sample_size: 100,
      auto_optimization: false,
      variants: [
        { id: 'control', name: 'Control', url: '', weight: 50 },
        { id: 'variant_a', name: 'Variant A', url: '', weight: 50 }
      ]
    });
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'running': return 'default';
      case 'paused': return 'secondary';
      case 'completed': return 'outline';
      case 'draft': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'running': return t('Active');
      case 'paused': return t('Paused');
      case 'completed': return t('Completed');
      case 'draft': return t('Draft');
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Optimization')}
        title={t('A/B Testing')}
        subtitle={t('Split traffic across variants to find what converts best.')}
        actions={
          <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              {t('Create A/B test')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('Create A/B test')}</DialogTitle>
              <DialogDescription>
                {t('Set up split testing to optimize conversions')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Basic Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('Test name')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('Landing page test')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('Campaign')}</Label>
                  <Select
                    value={formData.campaign_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, campaign_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('Select campaign')} />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('Description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('Test description')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('Test type')}</Label>
                  <Select
                    value={formData.test_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, test_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="landing_page">{t('Landing pages')}</SelectItem>
                      <SelectItem value="offer">{t('Offers')}</SelectItem>
                      <SelectItem value="path">{t('Traffic paths')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('Confidence level (%)')}</Label>
                  <Input
                    type="number"
                    value={formData.confidence_level}
                    onChange={(e) => setFormData(prev => ({ ...prev, confidence_level: parseFloat(e.target.value) || 0.95 }))}
                    min="80"
                    max="99"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('Minimum sample size')}</Label>
                  <Input
                    type="number"
                    value={formData.min_sample_size}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_sample_size: parseInt(e.target.value) || 0 }))}
                    min="50"
                  />
                </div>
              </div>

              <Separator />

              {/* Variants Configuration */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">{t('Test variants')}</Label>
                  <Button variant="outline" size="sm" onClick={addVariant}>
                    <Plus className="w-4 h-4 mr-1" />
                    {t('Add variant')}
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.variants.map((variant, index) => (
                    <div key={index} className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Badge variant={index === 0 ? "default" : "outline"}>
                            {index === 0 ? t('Control') : t('Variant {n}', { n: index })}
                          </Badge>
                          {index === 0 && <Trophy className="w-4 h-4 text-yellow-500" />}
                        </div>
                        {formData.variants.length > 2 && index > 0 && (
                          <Button variant="ghost" size="sm" aria-label={t('Remove variant')} title={t('Remove variant')} onClick={() => removeVariant(index)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>{t('Name')}</Label>
                          <Input
                            value={variant.name}
                            onChange={(e) => updateVariant(index, 'name', e.target.value)}
                            placeholder={t('Variant name')}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('URL (or pick below)')}</Label>
                          <Input
                            value={variant.url}
                            onChange={(e) => updateVariant(index, 'url', e.target.value)}
                            placeholder="https://example.com"
                          />
                          {formData.test_type === 'landing_page' && landingPages.length > 0 && (
                            <Select value="" onValueChange={(v) => updateVariant(index, 'url', v)}>
                              <SelectTrigger className="mt-1"><SelectValue placeholder={t('Pick landing page')} /></SelectTrigger>
                              <SelectContent>
                                {landingPages.map(lp => (
                                  <SelectItem key={lp.id} value={lp.url}>{lp.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {formData.test_type === 'offer' && offers.length > 0 && (
                            <Select value="" onValueChange={(v) => updateVariant(index, 'url', v)}>
                              <SelectTrigger className="mt-1"><SelectValue placeholder={t('Pick offer')} /></SelectTrigger>
                              <SelectContent>
                                {offers.map(o => (
                                  <SelectItem key={o.id} value={o.url}>{o.name} ({o.payout} {o.currency})</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>{t('Traffic weight (%)')}</Label>
                          <Input
                            type="number"
                            value={variant.weight}
                            onChange={(e) => updateVariant(index, 'weight', parseInt(e.target.value) || 0)}
                            min="1"
                            max="100"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-sm text-muted-foreground">
                  {t('Total weight')}: {formData.variants.reduce((sum, v) => sum + (v.weight || 0), 0)}%
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  {t('Cancel')}
                </Button>
                <Button onClick={handleCreateTest}>
                  {t('Create test')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        }
      />

      {/* Tests Grid */}
      <div className="grid gap-6">
        {tests.length === 0 ? (
          <Card>
            <CardContent style={{ padding: 0 }}>
              <XpnsrEmpty
                icon={Target}
                title={t('No A/B tests')}
                subtitle={t('Create split tests to optimize conversions')}
                cta={
                  <button
                    type="button"
                    className="xp-btn xp-btn-primary"
                    onClick={() => setCreateDialogOpen(true)}
                    data-testid="empty-create-abtest"
                  >
                    <Plus style={{ width: 12, height: 12 }} /> {t('Create A/B test')}
                  </button>
                }
              />
            </CardContent>
          </Card>
        ) : (
          tests.map((test) => (
            <Card key={test.id} className="border border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div>
                      <CardTitle className="text-lg">{test.name}</CardTitle>
                      <CardDescription>
                        {campaigns.find(c => c.id === test.campaign_id)?.name || t('Campaign not found')}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={getStatusVariant(test.status)}>
                      {getStatusLabel(test.status)}
                    </Badge>
                    {test.status === 'draft' || test.status === 'paused' ? (
                      <Button size="sm" aria-label={t('Start test')} title={t('Start test')} onClick={() => handleStartTest(test.id)}>
                        <Play className="w-4 h-4" />
                      </Button>
                    ) : test.status === 'running' ? (
                      <Button size="sm" variant="outline" aria-label={t('Pause test')} title={t('Pause test')} onClick={() => handlePauseTest(test.id)}>
                        <Pause className="w-4 h-4" />
                      </Button>
                    ) : null}
                    <Button size="sm" variant="outline" aria-label={t('Edit item')} title={t('Edit item')} onClick={() => setEditTest(test)} data-testid={`ab-edit-${test.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" aria-label={t('View test results')} title={t('View test results')} onClick={() => fetchTestResults(test.id)}>
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" aria-label={t('Delete A/B test')} title={t('Delete A/B test')} onClick={() => setDeleteTarget(test)} data-testid={`ab-delete-${test.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Visitors')}</Label>
                    <p className="font-semibold">{test.total_visitors || 0}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Conversions')}</Label>
                    <p className="font-semibold">{test.total_conversions || 0}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Significance')}</Label>
                    <p className="font-semibold">
                      {test.statistical_significance ? (
                        <Badge variant={Number(test.statistical_significance) >= (test.confidence_level || 95) ? 'default' : 'secondary'}>
                          {(Number(test.statistical_significance) || 0).toFixed(1)}%
                        </Badge>
                      ) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Variants')}</Label>
                    <p className="font-semibold">{test.variants?.length || 0}</p>
                  </div>
                </div>

                {test.description && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground">{test.description}</p>
                  </div>
                )}

                {test.status === 'running' && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm">{t('Progress to minimum sample')}</Label>
                      <span className="text-sm text-muted-foreground">
                        {test.total_visitors} / {test.min_sample_size}
                      </span>
                    </div>
                    <Progress value={test.min_sample_size ? (test.total_visitors / test.min_sample_size) * 100 : 0} className="w-full" />
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Results Dialog */}
      <Dialog open={resultsDialogOpen} onOpenChange={setResultsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('A/B test results')}</DialogTitle>
            <DialogDescription>
              {t('Variant performance analysis')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {testResults.length > 0 ? (
              <div className="space-y-4">
                {testResults.map((result, index) => (
                  <div key={result.variant_id} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Badge variant={result.is_control ? "default" : "outline"}>
                          {result.variant_name}
                        </Badge>
                        {result.is_winner && <Trophy className="w-4 h-4 text-yellow-500" />}
                        {result.is_control && <span className="text-xs text-muted-foreground">{t('Control')}</span>}
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold">
                          {(Number(result.conversion_rate) || 0).toFixed(2)}%
                        </div>
                        <div className="text-xs text-muted-foreground">{t('Conversion')}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Visitors')}</Label>
                        <p className="font-medium">{result.visitors}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Conversions')}</Label>
                        <p className="font-medium">{result.conversions}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Confidence interval')}</Label>
                        <p className="font-medium">
                          {(Number(result.confidence_interval.lower) || 0).toFixed(1)}% - {(Number(result.confidence_interval.upper) || 0).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t('Not enough data for analysis')}</p>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={() => setResultsDialogOpen(false)}>
                {t('Close')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ABTestEditDialog
        test={editTest}
        open={!!editTest}
        onOpenChange={(open) => { if (!open) setEditTest(null); }}
        onSaved={(upd) => setTests((prev) => prev.map((x) => (x.id === upd.id ? { ...x, ...upd } : x)))}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete A/B test')}
        description={t('This will permanently delete "{name}" and all its variants.').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDeleteTest(deleteTarget?.id)}
      />
    </div>
  );
}
